/**
 * Sessions API - Operacije za sesije
 */

import { httpClient } from '../client/httpClient';
import { logError } from '../utils/errorHandler';

export const sessionsApi = {
  /**
   * Dohvati listu svih sesija
   * Returns raw API response for backward compatibility
   */
  listSessions: async (): Promise<{ success: boolean; sessions: unknown[]; error?: string }> => {
    try {
      // Backend vraća { success: true, sessions: [...], total_count: N }
      interface ListSessionsResponse {
        success: boolean;
        sessions: unknown[];
        total_count?: number;
        error?: string;
      }

      const response = await httpClient.get<ListSessionsResponse>(
        '/api/training/list-sessions'
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to list sessions');
      }

      // httpClient vraća cijeli JSON kao ApiResponse, pristupamo sessions direktno
      const fullResponse = response as unknown as ListSessionsResponse;
      return {
        success: true,
        sessions: fullResponse.sessions || [],
      };
    } catch (error) {
      logError('sessionsApi.listSessions', error);
      throw error;
    }
  },

  /**
   * Preimenuj sesiju
   */
  renameSession: async (sessionId: string, newName: string): Promise<unknown> => {
    try {
      const response = await httpClient.post<unknown>(
        '/api/training/session-name-change',
        {
          sessionId,
          sessionName: newName,
        }
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to rename session');
      }

      return response;
    } catch (error) {
      logError('sessionsApi.renameSession', error);
      throw error;
    }
  },

  /**
   * Obriši pojedinačnu sesiju
   */
  deleteSession: async (sessionId: string): Promise<{ success: boolean }> => {
    try {
      const response = await httpClient.post(`/api/training/session/${sessionId}/delete`, {});

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete session');
      }

      return { success: true };
    } catch (error) {
      logError('sessionsApi.deleteSession', error);
      throw error;
    }
  },

  /**
   * Dohvati UUID sesije
   */
  getSessionUuid: async (sessionId: string): Promise<string | null> => {
    try {
      const response = await httpClient.get<{ sessionUuid: string }>(
        `/api/training/get-session-uuid/${sessionId}`
      );

      if (!response.success || !response.data?.sessionUuid) {
        return null;
      }

      return response.data.sessionUuid;
    } catch (error) {
      logError('sessionsApi.getSessionUuid', error);
      return null;
    }
  },

  /**
   * Obriši sve sesije
   */
  deleteAllSessions: async (): Promise<{ success: boolean; summary?: unknown }> => {
    try {
      const response = await httpClient.post<{ summary?: unknown }>(
        '/api/training/delete-all-sessions',
        {
          confirm_delete_all: true,
        }
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete all sessions');
      }

      return {
        success: true,
        summary: response.data?.summary,
      };
    } catch (error) {
      logError('sessionsApi.deleteAllSessions', error);
      throw error;
    }
  },
};
